import logIfNotInTestSuite from '../../../../utils/logIfNotInTestSuite';
import determineLinkType from './determineLinkType';
import linkTypeMapping from './linkTypeMapping';
import getVehicleDataFromElement from './getVehicleDataFromElement';
import getNameplateFromGlobalIncentivesPages from './getNameplateFromGlobalIncentivesPages';

const loggingPreamble = 'ws-tagging - forddirectnative - ';

/**
 * A mapping of link types that are only to be tracked once per VIN associated
 * with the event. The link types should map to arrays that will store the VINs
 * as events occur on the page. NOTE: this functionality depends on the link
 * type applied by the LinkTypeDecorator.
 */
const linkTypeOncePerVin = {};

/**
 * Returns the VIN of the vehicle associated with an event. If dataLayer
 * contains multiple vehicles, assumes user is on VLP and attempts to parse
 * VIN from DOM.
 *
 * @param {Element} element A reference to the element clicked
 * @returns {string} The type of link that was clicked.
 */
const getVin = (element) => {
	return window?.DDC?.dataLayer?.vehicles?.length === 1
		? window.DDC.dataLayer.vehicles[0].vin
		: $(element).parents('.hproduct').attr('data-vin');
};

/**
 * Checks clicked element against linkTypeOncePerVin object and
 * returns a boolean value.
 *
 * @param {object} eventData A reference to the eventData object.
 * @returns {boolean} If the event should be tracked
 */
const shouldTrack = (element, linkType) => {
	let track = true;
	if (linkTypeOncePerVin[linkType]) {
		const vin = getVin(element);

		if (vin) {
			track = !linkTypeOncePerVin[linkType].has(vin);
			linkTypeOncePerVin[linkType].add(vin);
		}
	}

	return track;
};

export default ({ element, eventData, mapping }) => {
	let { linkType } = eventData;

	if (!linkType) {
		linkType = determineLinkType(element);
	}

	if (linkType === 'generic' || !shouldTrack(element, linkType)) {
		const message =
			linkType === 'generic'
				? 'linkType is generic - ignoring link click event'
				: 'vin has already been tracked - ignoring link click event';
		logIfNotInTestSuite(`${loggingPreamble}${message}`);
		return false;
	}

	/*
	const linkTypeProps = this.dataMapper.map(
		`linkTypeMapping.${linkType}`
	);
	*/
	const payload = JSON.parse(JSON.stringify(linkTypeMapping[linkType]));

	// const initialDigitalData = { ...digitaldata };
	let modelYear;
	let nameplate;

	if (typeof payload !== 'object') {
		logIfNotInTestSuite(
			`${loggingPreamble}invalid mapping for ${linkType} link - ignoring link click event`
		);
		return false;
	}

	/*
	// apply static link type properties to digitaldata and store directCall
	digitaldata = this.dataMapper.mergeObjects(
		digitaldata,
		linkTypeProps
	);
	*/

	const directCall = payload.directCall || 'global-onclick';

	// remove directCall from digitaldata object
	delete payload.directCall;

	/*
	// remove variantName from digitaldata object
	if (digitaldata.page) {
		delete digitaldata.page.variantName;
	}
	*/

	// apply dynamic properties requiring eventData
	payload.onclick = payload.onclick || {};

	switch (linkType) {
		case 'incentive': {
			let incentiveName =
				element?.dataset?.incentiveName || eventData.text;

			try {
				if (!incentiveName) {
					if (!eventData.isSRP) {
						incentiveName = element?.firstElementChild?.textContent;
					} else if (element.textContent === 'Details') {
						incentiveName =
							element?.previousElementSibling?.textContent;
					} else if (element.className === 'price-value') {
						incentiveName =
							element?.parentElement?.previousElementSibling
								?.firstElementChild.textContent;
					} else {
						incentiveName = element.textContent;
					}
				}
			} catch (err) {
				logIfNotInTestSuite(`${loggingPreamble}err`);
				incentiveName = null;
			}
			if (eventData.isSRP) {
				payload.onclick.onclickLinkName += 'view offer';
			} else {
				payload.onclick.onclickLinkName += 'view incentives';
			}
			payload.onclick.onclick = `incentive:${incentiveName}`;

			// create nameplate for global incentives pages and concatenate pageName
			const url = window.location.href;
			const globalIncentivesNameplate =
				getNameplateFromGlobalIncentivesPages(url);

			if (globalIncentivesNameplate) {
				payload.page.pageName += `:${globalIncentivesNameplate}`;
				payload.onclick.onclickLinkName += `:${globalIncentivesNameplate}`;
			}

			break;
		}

		case 'model-e-buy-now-srp':
		case 'model-e-buy-now-vdp':
		case 'referral':
			payload.onclick.referredTo = eventData.url;
			break;
		case 'windowsticker-vlp': {
			const vehicleData = getVehicleDataFromElement(element);
			nameplate = vehicleData.nameplate;
			modelYear = vehicleData.modelYear;
			payload.vehicle = payload.vehicle || {};
			payload.vehicle.modelYear = modelYear;
			payload.vehicle.nameplate = nameplate;
			payload.onclick.onclick = `{inventoryType}:si:vls:view window sticker:${nameplate}`;
			payload.onclick.onclickLinkName += `:${nameplate}`;
			payload.page.hierarchy = `shopping tools:search inventory:{inventoryType}:${nameplate}`;
			break;
		}

		case 'finance-center': {
			const tabText =
				element && element.textContent
					? element.textContent
							.trim()
							.replace(/\s+/g, '-')
							.toLowerCase()
					: '';
			payload.onclick.onclick += `:${tabText}`;
			break;
		}
		case 'finance-application': {
			const financeAnchorNumber = eventData.url.slice(-1);
			payload.page.pageName += `:${financeAnchorNumber}`;
			break;
		}
		case 'payment-calculator': {
			const payCalcElem = element.className;
			const standardCalc = ':payment calculator:calculate';
			const drCalc = ':dr payment calculator:calculate';

			if (payCalcElem.indexOf('payment-summary-calculate') > -1) {
				payload.onclick.onclick += `${standardCalc}`;
				payload.onclick.onclickLinkName += `${standardCalc}`;
			} else {
				payload.onclick.onclick += `${drCalc}`;
				payload.onclick.onclickLinkName += `${drCalc}`;
			}
			break;
		}
		case 'collision-page-link': {
			let collisionClickType = 'collision guide';
			if (eventData.url === '#why-choose-section') {
				collisionClickType = 'why ford parts';
			} else if (eventData.url === '#rights-section') {
				collisionClickType = 'your rights';
			}
			payload.onclick.onclick = payload.onclick.onclick.replace(
				'{collisionClickType}',
				collisionClickType
			);
			payload.onclick.onclickLinkName =
				payload.onclick.onclickLinkName.replace(
					'{collisionClickType}',
					collisionClickType
				);
			break;
		}
		case 'accordion_delivery_1': {
			const accordionState = element.classList.contains('collapsed')
				? 'close'
				: 'open';
			payload.onclick.onclick = `pickup and delivery:content:accordion:${eventData.text}:${accordionState}`;
			break;
		}
		default:
			break;
	}

	// update page name related props if link click updated page name
	if (payload?.page?.pageName) {
		payload.page.pageNameNoVehicle = payload.page.pageName;
	}

	// apply vehicle nameplate information from clicked element if available
	nameplate =
		mapping.templateStrings.nameplate ||
		getVehicleDataFromElement(element).nameplate;

	// update digitaldata props that use nameplate with new nameplate
	if (nameplate && typeof nameplate === 'string') {
		nameplate = nameplate.toLowerCase();
		payload.page = payload.page || {};
		payload.page.pageName += `:${nameplate}`;
		payload.vehicle = payload.vehicle || {};
		payload.vehicle.nameplate = nameplate;

		if (payload.onclick.onclickLinkName) {
			payload.onclick.onclickLinkName =
				payload.onclick.onclickLinkName.replace(
					/({nameplate})/gi,
					nameplate
				);
		}
	}

	const { referralExit } = payload;
	if (referralExit) {
		// delete referralExit property from digital data as it's just needed to identify what events will have a referral exit
		delete payload.referralExit;
	}

	const payloads = [];

	/*
	// send link click event
	this.sendData(directCall, {
		digitaldata,
		event: eventData.event,
		linkType
	});
	*/

	payloads.push({
		directCall,
		linkType,
		payload
	});

	/*
	// send referral exit
	if (referralExit) {
		digitaldata = sendReferralExit(
			referralExit,
			initialDigitalData,
			eventData
		);
	}
	*/

	if (referralExit) {
		const referralExitPayload = linkTypeMapping[referralExit];

		if (referralExitPayload) {
			const { directCall: referralExitDirectCall } = referralExitPayload;

			delete referralExitPayload.directCall;

			payloads.push({
				directCall: referralExitDirectCall,
				linkType,
				payload: referralExitPayload
			});
		} else {
			logIfNotInTestSuite(`${loggingPreamble}referralExit has no data`);
		}
	}

	return payloads;
};
